import * as React from "react";
import { computed, observable } from "mobx"
import { Urls } from "../Urls";
import { observer } from "mobx-react";
import { AppStore } from "../Stores/AppStore";
import { FlagIcon } from "../Icons/FlagIcon";
import { Strings } from "../Stores/StringsStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { SaveButton } from "../Buttons/SaveButton";
import { CloseButton } from "../Buttons/CloseButton";
import { Area } from "../General/Area";
import { AreaBody } from "../General/AreaBody";
import { Toolbar } from "../General/Toolbar";
import * as SettingsManagement from "../SettingsManagement";
import { InputBooleanComponent } from "../Inputs/InputBooleanComponent";
import * as Utilities from "../Utilities";
import { DialogStore } from "../Stores/DialogStore";

@observer
export class LanguageSettingsEdit extends React.Component<{}, {}> {
	@observable model: SettingsManagement.LanguageDetails[] = [];
	@observable hash: string;
	@observable query = "";
	@observable selected = true;

	captions: string[] = [];
	queryTimeoutHandle: number;

	componentDidMount() {
		this.getModel();
		this.setTitle();
	}

	componentDidUpdate() {
		this.setTitle();
	}

	setTitle = () => {
		AppStore.setTitle(Strings.globalSettings.languageSettings.title);
	}

	@computed get canSave(): boolean {
		let canSave = this.hash != JSON.stringify(this.model);
		AppStore.isDirtyMode = canSave;
		AppStore.isDirtyChange = this.hash != JSON.stringify(this.model);
		AppStore.saveOnLeave = this.saveOnDirtyLeave;
		return canSave;
	}

	getModel() {
		AppStore.isBusyModelLoading = true;
		SettingsManagement.getLanguagesAsync().then(model => {
			this.model = model;
			this.captions = [];
			for (let i = 0; i < this.model.length; i++) {
				this.captions.push(model[i].caption.toLowerCase());
			}
			this.hash = JSON.stringify(this.model);
			NotificationStore.clearMessage();
			AppStore.isBusyModelLoading = false;
		}).catch(error => {
			DialogStore.showError(Strings.general.errorDialogTitle, error);
		});
	}

	handleSaveUi = () => {
		this.saveModel();
	}

	saveOnDirtyLeave = (onSaveCompleted: () => void) => {
		this.saveModel(onSaveCompleted);
	}


	private saveModel(onSaveCompleted?: () => void) {
		if (this.canSave) {
			AppStore.isBusy = true;
			let updateModel: SettingsManagement.LanguageUpdateInfo[] = [];
			for (let i = 0; i < this.model.length; i++) {
				let item = new SettingsManagement.LanguageUpdateInfo();
				item.name = this.model[i].name;
				item.isUserInterfaceLanguage = this.model[i].isUserInterfaceLanguage.value;
				item.isContentLanguage = this.model[i].isContentLanguage.value;
				updateModel.push(item);
			}

			SettingsManagement.saveLanguagesAsync(updateModel).then(model => {
				this.model = model;
				this.hash = JSON.stringify(this.model);
				AppStore.isBusy = false;
				NotificationStore.showSuccessMessage(Strings.general.saveSuccessMessage);
				AppStore.init();
				if (onSaveCompleted) {
					onSaveCompleted();
				}
			}).catch(error => {
				AppStore.isBusy = false;
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
		}
	}

	handleCancel = () => {
		AppStore.keepOnPage(Urls.home);
	}

	handleChange = () => {
		NotificationStore.clearMessage();
	}

	@computed get filteredModel(): SettingsManagement.LanguageDetails[] {
		let filtered: SettingsManagement.LanguageDetails[] = [];
		for (let i = 0; i < this.model.length; i++) {
			let item = this.model[i];
			let caption = this.captions[i];

			if ((!this.selected || item.isUserInterfaceLanguage.value || item.isContentLanguage.value) && (this.query.length == 0 || item.caption.toLowerCase().indexOf(this.query) >= 0)) {
				Utilities.DebugLog((this.query.length == 0 || caption.indexOf(this.query) >= 0) + " " + item.caption);
				filtered.push(item);
			}
		}
		return filtered;
	}

	handleQueryChange = (event: React.FormEvent<any>) => {
		this.query = ((event.target as any).value as string).toLowerCase();
		clearTimeout(this.queryTimeoutHandle);
		this.queryTimeoutHandle = setTimeout(() => {
		}, 300);
	}

	handleSelectedChange = (event: React.FormEvent<any>) => {
		this.selected = (event.target as any).checked as boolean;
	}

	render() {
		return <Area>
			<AreaBody loading={AppStore.isBusyModelLoading}>
				<Toolbar>
					<SaveButton onClick={this.handleSaveUi} disabled={!this.canSave} testId="saveButton" />
					<span className="buttongroup is--buttongroup-right">
						<CloseButton onClick={this.handleCancel} testId="closeButton" />
					</span>
				</Toolbar>
				<div className="area-grid__body-content">
					<div className="content-main">
						<h1>{Strings.globalSettings.languageSettings.title}</h1>

						<label>
							<span>{Strings.globalSettings.languageSettings.filterLabel}</span>
							<input type="text" value={this.query} onChange={this.handleQueryChange} data-test-id="filterInput" />
						</label>

						<label className="custom-radiocheck" data-test-id="showSelectedRadioCheck">
							<input type="checkbox" checked={this.selected} onChange={this.handleSelectedChange} />
							<span className="checkbox-style"></span>
							<span>{Strings.globalSettings.languageSettings.showSelectedLabel}</span>
						</label>
						<table data-test-id="languageItems" className="is--small-icons">
							<thead>
								<tr>
									<th />
									<th>{Strings.globalSettings.languageSettings.listHeaderNameTitle}</th>
									<th>{Strings.globalSettings.languageSettings.listHeaderInterfaceTitle}</th>
									<th>{Strings.globalSettings.languageSettings.listHeaderContentTitle}</th>
								</tr>
							</thead>
							<tbody>
								{this.filteredModel.map(item => {
									return <tr key={item.name} data-test-name="languageItem" data-test-id={item.name}>
										<td title={item.name}>
											<div className={"flag flag-" + item.region.toLowerCase()} />  
										</td>
										<td data-test-name="languageCaption">{item.caption}</td>
										<td data-test-name="isUserInterfaceLanguageRadioCheck"><InputBooleanComponent model={item.isUserInterfaceLanguage} /></td>
										<td data-test-name="isContentLanguageRadioCheck"><InputBooleanComponent model={item.isContentLanguage} /></td>
									</tr>;
								})}
							</tbody>
						</table>
					</div>
				</div>
			</AreaBody>
		</Area>;
	}
}


